import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieListItem from '@/components/feature/MovieListItem';
import Pagination from '@/components/feature/Pagination';
import EmptyState from '@/components/feature/EmptyState';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { movies as allMovies, type MovieType } from '@/mocks/movies';
import { CATEGORY_META, filterMovies, paginate, type SortValue } from '@/utils/movie';
import FilterBar from './components/FilterBar';

const PAGE_SIZE = 4;
const VALID_TYPES: MovieType[] = ['movie', 'tv', 'variety'];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<SortValue>('latest');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const isValid = !!type && VALID_TYPES.includes(type as MovieType);
  const meta = isValid ? CATEGORY_META[type as MovieType] : null;

  useDocumentMeta({
    title: meta ? meta.seoTitle : '分类 - 丝瓜视频',
    description: meta ? meta.seoDescription : '丝瓜视频影视分类，按类型浏览电影、电视剧与综艺节目。',
    keywords: meta ? `丝瓜视频,${meta.label},${meta.label}频道,影视分类` : '丝瓜视频,影视分类',
  });

  const result = useMemo(() => {
    if (!isValid) return [];
    return filterMovies({ type: type as MovieType, region, year, sort });
  }, [isValid, type, region, year, sort]);

  const { items, totalPages } = useMemo(() => paginate(result, page, PAGE_SIZE), [result, page]);

  useEffect(() => {
    setPage(1);
  }, [region, year, sort, type]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const handleReset = () => {
    setRegion('全部');
    setYear('全部');
    setSort('latest');
    setPage(1);
  };

  if (!isValid || !meta) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1360px] px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="影视内容未找到"
            description="该分类不存在或已下线，返回首页看看其他精彩内容吧。"
          />
          <div className="mt-6 flex justify-center">
            <Link to="/" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600">
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1360px] px-4 py-6 md:px-6 md:py-10">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-foreground-900">{meta.label}</span>
        </nav>

        <header className="mt-4 flex flex-col gap-3 border-b border-background-200 pb-5 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              {meta.channel}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">{meta.description}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-stack-line" /> 共 <strong className="text-primary-600">{allMovies.filter((m) => m.type === type).length}</strong> 部内容
          </div>
        </header>

        <div className="mt-6">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="mb-3 flex w-full items-center justify-between rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-800 md:hidden"
          >
            <span className="inline-flex items-center gap-2"><i className="ri-filter-3-line" /> 筛选条件</span>
            <i className={filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </button>

          <div className={filterOpen ? 'block' : 'hidden md:block'}>
            <FilterBar
              region={region}
              year={year}
              sort={sort}
              onRegion={setRegion}
              onYear={setYear}
              onSort={setSort}
              onReset={handleReset}
            />
          </div>
        </div>

        <div className="mt-6">
          {items.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {items.map((movie) => (
                  <MovieListItem key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-search-eye-line"
              title="抱歉，没有找到相关影片"
              description="换个筛选条件试试，或者重置筛选查看全部内容。"
              actionLabel="重置筛选"
              onAction={handleReset}
            />
          )}
        </div>
      </div>
    </SiteLayout>
  );
}